<script setup lang="ts">
import { generateForm } from "@/helpers/generators";
import type { ContentFormStoryblok } from "~/types/storyblok";
import type { ErrorObject } from "@vuelidate/core";
import { useVuelidate } from "@vuelidate/core";
import { required, email, url } from "~/lib/validator/validators";
import type { InputType } from "@kippie/ui";

const props = defineProps<{ blok: ContentFormStoryblok }>();

const isLoading = ref<boolean>(false);
const formSent = ref<boolean>(false);
const form = ref<{ [k: string]: any }>(generateForm(props.blok.inputs));

const inputType = (type: string): string => {
	if (type === "email") {
		return "text";
	}
	if (type === "url") {
		return "text";
	}
	return type;
};

const validator = useVuelidate(
	{
		...props.blok.inputs.reduce((acc, input) => {
			acc[input.name] = {};
			if (input.required) {
				acc[input.name] = { required };
			}

			if (input.type === "email") {
				acc[input.name] = { ...acc[input.name], email };
			}

			if (input.type === "url") {
				acc[input.name] = { ...acc[input.name], url };
			}

			return acc;
		}, {} as any)
	},
	form
);

async function handleSubmit(): Promise<void> {
	isLoading.value = true;
	await validator.value.$validate();
	if (validator.value.$error) {
		isLoading.value = false;
		return;
	}
	try {
		await $fetch("/api/mail/customer-service", {
			method: "POST",
			body: JSON.stringify({
				...form.value,
				recipient: props.blok.emailRecipient
			})
		});
		form.value = generateForm(props.blok.inputs);
		formSent.value = true;
		push.success({
			message: "Bedankt voor je bericht!"
		});
		await validator.value.$reset();
	} catch (err) {
		console.error(err);
		push.error({
			message: "Er is iets misgegaan bij het verzenden.\n\nKlik hier om het opnieuw te proberen.",
			props: {
				onClick: () => {
					handleSubmit();
				}
			}
		});
	} finally {
		isLoading.value = false;
	}
}
</script>

<template>
	<div v-editable="blok">
		<form class="space-y-6 pt-4" @submit.prevent="handleSubmit">
			<KippieInput
				v-for="input in blok.inputs"
				:key="input.name"
				:disabled="isLoading"
				v-model="form[input.name]"
				:type="inputType(input.type) as InputType"
				:label="input.label"
				:placeholder="input.placeholder"
				:name="input.name"
				:error="validator[input.name]?.$error"
				:help="validator[input.name]?.$errors?.map((e: ErrorObject) => e.$message).join(', ')"
			/>
			<KippieButton :disabled="isLoading || formSent" type="submit" block>
				{{ formSent ? "Formulier is verstuurd" : blok.submitButton }}
			</KippieButton>
		</form>
	</div>
</template>
